import React, { lazy } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router";

import { Brand, getConfig } from "merchant-portal-frontend/src/config";
import {
	AccountSettingsScope,
	CartScope,
	LocationsScope,
	UserManagementScope,
} from "merchant-portal-frontend/src/global.constants";
import { shouldUseFakeAuth } from "merchant-portal-frontend/src/model/auth";
import { AuthenticatedRoutes } from "merchant-portal-pages/AuthenticatedRoutes";
import { AuthorizedRoute, AuthorizedRouteProps } from "merchant-portal-pages/AuthorizedRoute";
import LoadingPage from "merchant-portal-pages/LandingPage/LoadingPage";
import { routes } from "merchant-portal-pages/routes";

const RootPage = lazy(() => import("merchant-portal-pages/RootPage"));
const LandingPage = lazy(() => import("merchant-portal-pages/LandingPage/LandingPage"));
const HomePage = lazy(() => import("merchant-portal-pages/HomePage/HomePage"));

// Landing page components
const LoginPage = lazy(() => import("merchant-portal-pages/LandingPage/LoginPage"));
const AdminPage = lazy(() => import("merchant-portal-pages/LandingPage/AdminPage"));
const CreatePasswordPage = lazy(
	() => import("merchant-portal-pages/LandingPage/CreatePasswordPage"),
);
const ResetPasswordPage = lazy(
	() => import("merchant-portal-pages/LandingPage/ResetPasswordPage"),
);

// Home page components
const TransactionsListWrapper = lazy(
	() => import("merchant-portal-components/TransactionsView/TransactionListWrapper"),
);
const AccountSettings = lazy(
	() => import("merchant-portal-components/AccountSettings/AccountSettings"),
);
const DataInsights = lazy(
	() => import("merchant-portal-components/DataInsightsView/DataInsights"),
);
const SettlementReport = lazy(
	() => import("merchant-portal-components/SettlementReportView/SettlementReport"),
);
const UserListWrapper = lazy(
	() => import("merchant-portal-components/UserManagementView/UsersListWrapper"),
);
const TargetingExport = lazy(
	() => import("merchant-portal-components/TargetingExport/TargetingExport"),
);
const Carts = lazy(() => import("merchant-portal-components/Carts/CartsList"));

const Locations = lazy(() => import("merchant-portal-components/Locations/Locations"));

const ViewEditCart = lazy(() => import("merchant-portal-components/Carts/ViewEditCart"));

const CreateCart = lazy(() => import("merchant-portal-components/Carts/CreateCart"));

const PageNotFound = lazy(() => import("merchant-portal-pages/PageNotFound/PageNotFound"));

// Account Settings
const AccessKeys = lazy(
	() => import("merchant-portal-components/AccountSettings/AccessKeys/AccessKeys"),
);
const BankInfo = lazy(
	() => import("merchant-portal-components/AccountSettings/BankInformation/BankInformation"),
);
const PaymentProducts = lazy(
	() => import("merchant-portal-components/AccountSettings/PaymentProducts/PaymentProducts"),
);
const GeneralSettings = lazy(
	() => import("merchant-portal-components/AccountSettings/GeneralSettings/GeneralSettings"),
);
const CheckoutDesign = lazy(
	() => import("merchant-portal-components/AccountSettings/CheckoutDesign/CheckoutDesign"),
);
const PlacementDesign = lazy(
	() => import("merchant-portal-components/AccountSettings/PlacementSettings/PlacementSettings"),
);
const EmailNotifications = lazy(
	() =>
		import("merchant-portal-components/AccountSettings/EmailNotifications/EmailNotifications"),
);
const InternalSettings = lazy(
	() => import("merchant-portal-components/AccountSettings/InternalSettings/InternalSettings"),
);
const FundingSettings = lazy(
	() => import("merchant-portal-components/AccountSettings/FundingSettings/FundingSettings"),
);
const LocationVerificationCodes = lazy(
	() =>
		import("merchant-portal-components/AccountSettings/LocationVerificationCodes/LocationVerificationCodes"),
);
const MerchantIdValidation = lazy(
	() =>
		import("merchant-portal-components/AccountSettings/MerchantIDValidation/MerchantIDValidation"),
);

export const createLandingPageRoutes = () => [
	{
		element: <LandingPage />,
		children: [
			{
				path: "login",
				element: shouldUseFakeAuth() ? <AdminPage /> : <LoginPage />,
			},
			{
				path: "activate/:activationToken",
				element: <CreatePasswordPage />,
			},
			{
				path: "password/reset/:activationToken",
				element: <ResetPasswordPage />,
			},
		],
	},
	{
		path: "implicit/callback",
		element: <LoadingPage />,
	},
	{
		path: "logout/callback",
		element: <Navigate to={{ pathname: routes.login }} state={{}} />,
	},
];

export const createCartRoutes = () => {
	const brand = getConfig<Brand>("brand");

	return {
		element: (
			<AuthorizedRoute
				requiredScope={CartScope.ViewCartAccess}
				requiredProgramEnrollment="ecommerce"
				additionalRequirements={brand !== Brand.Rbc}
			/>
		),
		children: [
			{
				path: "carts",
				element: <Carts />,
			},
			{
				element: <AuthorizedRoute requiredScope={CartScope.CreateCartAccess} />,
				children: [
					{
						path: "carts/create",
						element: <CreateCart />,
					},
				],
			},
			{
				path: "carts/:cartId",
				element: <ViewEditCart />,
			},
		],
	};
};

export const createLocationsRoutes = () => {
	const brand = getConfig<Brand>("brand");

	return {
		element: (
			<AuthorizedRoute
				requiredScope={LocationsScope.ViewLocationsAccess}
				requiredFeatureFlag="isLocationPageEnabled"
				additionalRequirements={brand !== Brand.Rbc}
			/>
		),
		children: [
			{
				path: "locations",
				element: <Locations />,
			},
		],
	};
};

export const createAccountSettingsRoutes = () => {
	const accountSettingRoutes: (RouteObject & AuthorizedRouteProps)[] = [
		{ path: "account-settings", element: <GeneralSettings /> },
		{ path: "general-settings", element: <GeneralSettings /> },
		{
			path: "checkout-design",
			element: <CheckoutDesign />,
			requiredScope: AccountSettingsScope.CheckoutDesignAccess,
			requiredProgramEnrollment: "checkout-sdk-access",
		},
		{
			path: "placement-design",
			element: <PlacementDesign />,
			requiredScope: AccountSettingsScope.PlacementSettingsAccess,
			requiredProgramEnrollment: "ecommerce",
		},
		{
			path: "access-keys",
			element: <AccessKeys />,
			requiredScope: AccountSettingsScope.ApiKeysAccess,
		},
		{
			path: "email-notifications",
			element: <EmailNotifications />,
			requiredScope: AccountSettingsScope.EmailNotificationSettingsReadAccess,
		},
		{
			path: "payment-products",
			element: <PaymentProducts />,
			requiredScope: AccountSettingsScope.PaymentProductsReadAccess,
		},
		{
			path: "internal-settings",
			element: <InternalSettings />,
			requiredScope: AccountSettingsScope.InternalSettingsAccess,
		},
		{
			path: "bank-information",
			element: <BankInfo />,
			requiredScope: AccountSettingsScope.BankInformationAccess,
		},
		{
			path: "funding-settings",
			element: <FundingSettings />,
			requiredScope: AccountSettingsScope.FundingSettingsAccess,
		},
		{
			path: "location-verification-codes",
			element: <LocationVerificationCodes />,
			requiredScope: AccountSettingsScope.LocationVerificationCodesAccess,
			requiredProgramEnrollment: "instore-virtual-card",
		},
		{
			path: "merchant-id-validation",
			element: <MerchantIdValidation />,
			requiredScope: AccountSettingsScope.MerchantIdValidationAccess,
			requiredProgramEnrollment: "instore-virtual-card",
			requiredFeatureFlag: "isMerchantIdValidationPageEnabled",
		},
	];

	return {
		element: <AuthorizedRoute requiredScope={AccountSettingsScope.PageAccess} />,
		children: [
			{
				element: <AccountSettings />,
				children: accountSettingRoutes.map(
					({ path, element, ...authorizeRouteProps }) => ({
						element: <AuthorizedRoute {...authorizeRouteProps} />,
						children: [
							{
								path,
								element,
							},
						],
					}),
				),
			},
		],
	};
};

export const createHomePageRoutes = () => {
	const brand = getConfig<Brand>("brand");

	return [
		{
			element: <HomePage />,
			children: [
				{
					index: true,
					element: <TransactionsListWrapper />,
				},
				{
					path: "transactions",
					element: <TransactionsListWrapper />,
				},
				{
					path: "data-insights",
					element: <DataInsights />,
				},
				{
					element: <AuthorizedRoute additionalRequirements={brand !== Brand.Rbc} />,
					children: [
						{
							path: "settlement-report",
							element: <SettlementReport />,
						},
					],
				},
				{
					element: (
						<AuthorizedRoute requiredScope={UserManagementScope.PageReadAccess} />
					),
					children: [
						{
							path: "user-management",
							element: <UserListWrapper />,
						},
					],
				},
				{
					element: <AuthorizedRoute additionalRequirements={brand !== Brand.Rbc} />,
					children: [
						{
							path: "targeting-export",
							element: <TargetingExport />,
						},
					],
				},
				createAccountSettingsRoutes(),
				createCartRoutes(),
				createLocationsRoutes(),
				{ path: "*", element: <PageNotFound /> },
			],
		},
	];
};

export const createRoutes = () => {
	return [
		{
			path: "/",
			element: <RootPage />,
			children: [
				...createLandingPageRoutes(),
				{
					element: <AuthenticatedRoutes />,
					children: createHomePageRoutes(),
				},
			],
		},
	];
};

export const createRouter = () => createBrowserRouter(createRoutes());
